/**
 * An email address's canonical form, as the relay takes it (part 29a, table EM; the relay's `auth/email.ts`, which a
 * property test holds this to): the device keys the highest KDF parameters seen by it (C7), so every spelling of one
 * address meets the same floor, and refuses an address the relay would refuse before sending it.
 */

/** The longest address and local part. */
const MAX_EMAIL = 254;
const MAX_LOCAL = 64;

/** A domain label: letters, digits and inner hyphens. */
const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/u;
/** Controls, spaces of any kind, and the characters an address can't hold unquoted. */
const FORBIDDEN_LOCAL = /[\p{Cc}\p{Cf}\p{Z}\s@"(),:;<>[\\\]]/u;

/** An address in canonical form. Only {@link canonicalEmail} makes one. */
export type Email = string & { readonly __brand: 'Email' };

function isAddress(address: string): address is Email {
  if (address.length > MAX_EMAIL) return false;
  const at = address.indexOf('@');
  if (at < 0 || at !== address.lastIndexOf('@')) return false;
  const local = address.slice(0, at);
  const labels = address.slice(at + 1).split('.');
  if (local.length === 0 || local.length > MAX_LOCAL || FORBIDDEN_LOCAL.test(local)) return false;
  return labels.length >= 2 && labels.every((label) => LABEL.test(label));
}

/** EM1–EM4: `text` trimmed, lowercased and NFC-normalised, if it is then an address; null otherwise. */
export function canonicalEmail(text: string): Email | null {
  const address = text.trim().toLowerCase().normalize('NFC');
  return isAddress(address) ? address : null;
}
